<template>
  <div class="donation-ranking-page">
    <!-- 顶部导航栏 -->
    <van-nav-bar title="捐款榜单" left-arrow @click-left="$router.back()" />

    <!-- 捐款统计卡片 -->
    <div class="stats-card">
      <div class="stats-item">
        <div class="stats-number">
          <span class="number-value">{{ donorCount }}</span>
          <span class="number-unit">人</span>
        </div>
        <div class="stats-label">已捐款人数</div>
      </div>
      <div class="stats-divider"></div>
      <div class="stats-item">
        <div class="stats-number">
          <span class="number-value">{{ totalAmount }}</span>
          <span class="number-unit">元</span>
        </div>
        <div class="stats-label">累计捐款</div>
      </div>
    </div>

    <!-- 捐款列表 -->
    <div class="donation-list-card">
      <div class="card-header">
        <div class="header-title">
          <div class="title-badge">
            <van-icon name="orders-o" />
          </div>
          <span class="title-text">捐款记录</span>
        </div>
      </div>
      <van-list
        v-model="loading"
        :finished="finished"
        finished-text="没有更多了"
        @load="loadList"
      >
        <div class="donation-list">
          <div
            v-for="(item, index) in donationList"
            :key="index"
            class="donation-item"
          >
            <div class="item-main">
              <div class="item-left">
                <div class="donor-name">{{ item.name }}</div>
                <div class="donor-grade" v-if="item.grade">{{ item.grade }}届</div>
                <div class="donor-grade" v-else>未知届数</div>
              </div>
              <div class="item-right">
                <div class="donation-amount">
                  <span class="amount-symbol">￥</span>
                  <span class="amount-value">{{ item.amount }}</span>
                </div>
              </div>
            </div>
            <div class="item-footer">
              <div class="footer-left">
                <van-icon name="clock-o" class="time-icon" />
                <span class="donation-time">{{ item.date }}</span>
                <span v-if="item.certNo" class="cert-no">证书编号：{{ item.certNo }}</span>
              </div>
            </div>
          </div>
        </div>
      </van-list>
      <div v-if="!loading && donationList.length === 0" class="empty-state">
        <div class="empty-icon">
          <van-icon name="records-o" />
        </div>
        <p class="empty-text">暂无捐款记录</p>
        <p class="empty-subtext">快去献出您的一份爱心吧</p>
        <div class="empty-action" @click="goDonate">立即捐款</div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request';

export default {
  name: 'DonationRanking',
  data() {
    return {
      donorCount: 0,
      totalAmount: '0.00',
      donationList: [],
      loading: false,
      finished: false,
      pageNum: 1,
      pageSize: 20,
    };
  },
  created() {
    this.loadStats();
  },
  methods: {
    async loadStats() {
      try {
        const res = await request.get('/app/donation/stats');
        if (res.code === 200 || res.code === 0) {
          const data = res.data || {};
          this.donorCount = data.donorCount || 0;
          this.totalAmount = this.formatAmount(data.totalAmount || 0);
        }
      } catch (e) {
        console.error('加载统计数据失败', e);
      }
    },
    async loadList() {
      if (this.finished) return;
      try {
        const res = await request.get('/app/donation/list', {
          pageNum: this.pageNum,
          pageSize: this.pageSize,
        });
        if (res.code === 200 || res.code === 0) {
          const rows = res.data.rows || [];
          const total = res.data.total || 0;
          const mapped = rows.map(item => ({
            name: item.name || '匿名用户',
            grade: item.graveName || '',
            amount: item.amount || 0,
            date: item.payDate || '',
            certNo: item.certNo || '',
          }));
          this.donationList.push(...mapped);
          this.loading = false;
          if (this.donationList.length >= total) {
            this.finished = true;
          } else {
            this.pageNum++;
          }
        } else {
          this.loading = false;
          this.finished = true;
        }
      } catch (e) {
        console.error('加载捐款列表失败', e);
        this.loading = false;
        this.finished = true;
      }
    },
    formatAmount(val) {
      if (!val) return '0.00';
      const num = parseFloat(val);
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    goDonate() {
      this.$router.push('/donate');
    },
  },
};
</script>

<style lang="less" scoped>
@primary: #8B1A2B;
@primary-light: #B83245;
@primary-dark: #6B1321;
@gold: #C9A96E;
@gold-light: #DCC696;
@bg: #FAF7F2;
@card-bg: #FFFFFF;
@text-primary: #2C1810;
@text-secondary: #6B5B50;
@text-tertiary: #9B8B7B;
@border: #EDE5DC;
@shadow: 0 4px 20px rgba(44, 24, 16, 0.06);

.donation-ranking-page {
  min-height: 100vh;
  background: @bg;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

/deep/ .van-nav-bar {
  background: @primary !important;

  .van-nav-bar__title {
    color: #fff;
    font-weight: 600;
  }

  .van-icon {
    color: #fff;
  }
}

.stats-card {
  margin: 12px 16px;
  padding: 24px 16px;
  background: linear-gradient(135deg, @primary, @primary-dark);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  position: relative;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(139, 26, 43, 0.25);

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(201, 169, 110, 0.15), transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -10%;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, rgba(201, 169, 110, 0.1), transparent 70%);
    pointer-events: none;
  }

  .stats-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;

    .stats-number {
      display: flex;
      align-items: baseline;
      margin-bottom: 8px;

      .number-value {
        font-size: 28px;
        font-weight: 800;
        color: #fff;
        letter-spacing: 1px;
        font-family: 'DIN Alternate', 'Helvetica Neue', Arial, sans-serif;
      }

      .number-unit {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.7);
        margin-left: 3px;
      }
    }

    .stats-label {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.8);
    }
  }

  .stats-divider {
    width: 1px;
    height: 40px;
    background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.3), transparent);
  }
}

.donation-list-card {
  margin: 12px 16px;
  padding: 16px;
  background: @card-bg;
  border-radius: 16px;
  box-shadow: @shadow;

  .card-header {
    margin-bottom: 12px;

    .header-title {
      display: flex;
      align-items: center;

      .title-badge {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        background: linear-gradient(135deg, @gold-light, @gold);
        border-radius: 8px;
        margin-right: 8px;
        color: #fff;
        font-size: 14px;
      }

      .title-text {
        font-size: 16px;
        font-weight: 700;
        color: @text-primary;
      }
    }
  }

  .donation-list {
    .donation-item {
      padding: 14px 0;
      border-bottom: 1px solid @border;

      &:last-child {
        border-bottom: none;
      }

      .item-main {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;

        .item-left {
          display: flex;
          align-items: center;
          gap: 8px;

          .donor-name {
            font-size: 15px;
            font-weight: 600;
            color: @text-primary;
          }

          .donor-grade {
            font-size: 11px;
            color: @text-tertiary;
            background: @bg;
            padding: 1px 8px;
            border-radius: 10px;
          }
        }

        .item-right {
          .donation-amount {
            display: flex;
            align-items: baseline;

            .amount-symbol {
              font-size: 13px;
              color: @primary;
              font-weight: 600;
            }

            .amount-value {
              font-size: 18px;
              font-weight: 800;
              color: @primary;
              font-family: 'DIN Alternate', 'Helvetica Neue', Arial, sans-serif;
            }
          }
        }
      }

      .item-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .footer-left {
          display: flex;
          align-items: center;
          gap: 4px;

          .time-icon {
            font-size: 12px;
            color: @text-tertiary;
          }

          .donation-time {
            font-size: 12px;
            color: @text-tertiary;
          }

          .cert-no {
            font-size: 12px;
            color: @primary;
            margin-left: 8px;
            font-weight: 500;
          }
        }
      }
    }
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 0;

    .empty-icon {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: @bg;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 12px;

      .van-icon {
        font-size: 28px;
        color: @text-tertiary;
      }
    }

    .empty-text {
      font-size: 15px;
      font-weight: 600;
      color: @text-secondary;
      margin: 0 0 6px;
    }

    .empty-subtext {
      font-size: 13px;
      color: @text-tertiary;
      margin: 0 0 20px;
    }

    .empty-action {
      padding: 10px 32px;
      background: linear-gradient(135deg, @primary-light, @primary);
      color: #fff;
      font-size: 14px;
      font-weight: 600;
      border-radius: 20px;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(139, 26, 43, 0.3);

      &:active {
        transform: scale(0.97);
      }
    }
  }
}
</style>
